/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toastError, toastSuccess } from '../../util/toastUtil'
import { useCustomAxios } from '../../axiosInstance'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { evalFormStatements, waiverStatements } from '../../models/static_data'
import { LetterForm, initLetterForm } from '../../models/data'
import Loader from '../../components/Loader/loader'
import { LETTERFORM_ENDPOINTS } from '../../constants/apiConstants'
import { NAVIGATION_LINKS } from '../../constants/routeConstants'
import InputAutoSize_EForm from '../../components/InputAutoSize_EForm/InputAutoSize_EForm'

function EvaluationLetterForm({ setLoadingDataGlobal }) {
    const axios = useCustomAxios()
    const navigate = useNavigate()
    const [evalForm, setEvalForm] = useState(initLetterForm)
    const [qaList1_2, setQAList1_2] = useState([])
    const [qaList, setQAList] = useState([])
    const [validationErrorList1_2, setValidationErrorList1_2] = useState([])
    const [validationErrorList, setValidationErrorList] = useState([])
    const [loadingData, setLoadingData] = useState(false)
    const { id } = useParams()

    const saveIntervalMinutes = 10
    const saveIntervalMilliseconds = saveIntervalMinutes * 60 * 1000

    useEffect(() => {
        // Set up an interval to save data every 5 minutes
        const intervalId = setInterval(() => {
            saveOnInterval()
        }, saveIntervalMilliseconds)

        // Clean up the interval when the component unmounts
        return () => {
            clearInterval(intervalId)
        }
    }, [evalForm])

    useEffect(() => {
        let isSubscribed = true
        window.scrollTo(0, 0)

        const questionIds = [
            1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
        ]

        const getData = async (Id) => {
            setLoadingData(true)

            try {
                const response = await axios.get(
                    LETTERFORM_ENDPOINTS.GETFORM + Id
                )
                const data = response.data

                if (isSubscribed && data) {
                    const receivedQuestionIds = data.qaList.map(
                        (qa) => qa.letterFormQuestionId
                    )
                    const allQuestionsPresent = questionIds.every((id) =>
                        receivedQuestionIds.includes(id)
                    )

                    if (allQuestionsPresent) {
                        setEvalForm(new LetterForm(data))
                        setQAList1_2(data.qaList.slice(0, 2))
                        setQAList(data.qaList.slice(2))

                        let errList = []
                        for (let qa of data.qaList) {
                            errList.push(false)
                        }
                        setValidationErrorList1_2(errList.slice(0, 2))
                        setValidationErrorList(errList.slice(2))
                        setLoadingData(false)
                    } else {
                        getData(Id) // Retry if not all questionIds are present
                    }
                }
            } catch (err) {
                setLoadingData(false)
                if (err.response) {
                    const { error, message } = err.response.data
                    toastError(message || 'Letter not found.')
                    if (
                        error === 'letter_not_found' ||
                        error === 'id_mismatch'
                    ) {
                        navigate(NAVIGATION_LINKS.DASHBOARD)
                    }
                } else {
                    toastError('An error occurred. Please try again.')
                }
            }
        }

        getData(id)

        return () => {
            isSubscribed = false
        }
    }, [])

    const saveOnInterval = async () => {
        if (evalForm.isSubmitted) {
            return
        }

        const data = evalForm
        data.qaList = qaList1_2.concat(qaList)
        data.qaList = data.qaList.filter(
            (question) => !question.isQuestionWithoutAnswer
        )

        try {
            await axios.post(LETTERFORM_ENDPOINTS.SAVEFORM + id, data)
        } catch (err) {
            if (err.response) {
                const { message } = err.response.data
                // toastError(message || 'Letter was not auto saved successfully!')
            } else {
                // toastError('An error occurred. Please try again.')
            }
        }
    }

    const handleChange = (idx, event, range) => {
        const newData = event.target.value
        const hasError =
            newData === null ||
            (typeof newData === 'string' && newData.trim().length === 0)

        // Determine if an update is necessary for text input or selection change

        if (
            (range === 0 && qaList1_2[idx].answer !== newData) ||
            (range === 1 && qaList[idx].answer !== newData)
        ) {
            const updateList = (list, errList) => {
                const newList = [...list]
                const newErrList = [...errList]
                newList[idx].answer = newData
                newErrList[idx] = hasError
                return [newList, newErrList]
            }

            if (range === 0) {
                const [newList, newErrList] = updateList(
                    qaList1_2,
                    validationErrorList1_2
                )
                setQAList1_2(newList)
                setValidationErrorList1_2(newErrList)
            } else if (range === 1) {
                const [newList, newErrList] = updateList(
                    qaList,
                    validationErrorList
                )
                setQAList(newList)
                setValidationErrorList(newErrList)
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Merge all answers before validation
        // const mergedQAList = qaList1_2.concat(qaList)
        // evalForm.qaList = mergedQAList

        const mergedQAList = qaList1_2.concat(qaList)
        evalForm.qaList = mergedQAList.filter(
            (question) => !question.isQuestionWithoutAnswer
        )

        // Validate form
        let ret1 = validateForm(
            validationErrorList1_2,
            setValidationErrorList1_2,
            qaList1_2
        )
        let ret2 = validateForm(
            validationErrorList,
            setValidationErrorList,
            qaList
        )

        if (!ret1 || !ret2) {
            toastError('Please answer all Required fields.')
            return
        }

        // Set submission flag and save form
        evalForm.isSubmitted = true
        setLoadingData(true)

        try {
            await axios.post(LETTERFORM_ENDPOINTS.SAVEFORM + id, evalForm)
            setLoadingData(false)
            toastSuccess('Letter was submitted successfully!')
            navigate(NAVIGATION_LINKS.DASHBOARD)
        } catch (err) {
            evalForm.isSubmitted = false
            setLoadingData(false)
            if (err.response) {
                const { message } = err.response.data
                toastError(message || 'Letter was not submitted successfully!')
            } else {
                toastError('An error occurred. Please try again.')
            }
        }
    }

    const handleSave = async (e) => {
        e.preventDefault()

        // Merge all answers before saving
        const mergedQAList = qaList1_2.concat(qaList)
        evalForm.qaList = mergedQAList.filter(
            (question) => !question.isQuestionWithoutAnswer
        )

        setLoadingData(true)

        try {
            await axios.post(LETTERFORM_ENDPOINTS.SAVEFORM + id, evalForm)
            setLoadingData(false)
            toastSuccess('Letter was saved successfully!')
            navigate(NAVIGATION_LINKS.DASHBOARD)
        } catch (err) {
            setLoadingData(false)
            if (err.response) {
                const { message } = err.response.data
                toastError(message || 'Letter was not saved successfully!')
            } else {
                toastError('An error occurred. Please try again.')
            }
        }
    }

    const validateForm = (ErrorList, setErrorList, data) => {
        let ret = true
        let errList = [...ErrorList]
        for (let i = 0; i < data.length; i++) {
            // if (data[i].letterFormQuestionId === 4) {
            //     continue
            // }
            if (data[i].isQuestionWithoutAnswer) {
                continue
            }
            if (data[i].answer === null || data[i].answer.length === 0) {
                errList[i] = true
                ret = false
            }
        }
        setErrorList(errList)
        return ret
    }

    const getQAFromList = (qa, index, range, ErrorList) => {
        return (
            <div>
                <InputAutoSize_EForm
                    isQuestionWithAnswer={!qa.isQuestionWithoutAnswer}
                    question={qa}
                    validationError={ErrorList[index]}
                    onChange={(e) => handleChange(index, e, range)}
                    disabled={evalForm.isSubmitted}></InputAutoSize_EForm>
            </div>
        )
    }
    return (
        <div style={{ margin: '5em', fontSize: '16px' }}>
            <Loader open={loadingData} />
            <h5 style={{ textAlign: 'center', fontWeight: 'bold' }}>
                STANDARDIZED EVALUATION FORM
            </h5>
            <h5 style={{ textAlign: 'center' }}>
                Dental, Medical, and Podiatry Applicants
            </h5>
            <div>
                <p style={{ fontSize: 14, fontWeight: 'bold', color: 'red' }}>
                    PLEASE NOTE: The system will automatically log you out after
                    an hour of inactivity.
                </p>
                {/* <span style={{ fontSize: 14, color: 'blue' }}>
                    Deadline for submission: September 15.
                </span> */}
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <div>
                                Name of Evaluator :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.evaluatorName}
                                </span>
                            </div>
                            <div>
                                Evaluator's Email :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.evaluatorEmail}
                                </span>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div>
                                Name of Applicant :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.applicantName}
                                </span>
                            </div>
                            <div>
                                Applicant Mobile Number :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.applicantPhoneNumber}
                                </span>
                            </div>
                            <div>
                                TMDSAS Applicant ID# :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.applicantUserId}
                                </span>
                            </div>
                            <div>
                                Applicant Email :{' '}
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.applicantEmail}
                                </span>
                            </div>
                        </Grid>
                    </Grid>
                </Box>

                <br />

                <div>
                    <div>
                        {qaList1_2.map((qa, index) => (
                            <div key={index}>
                                {getQAFromList(
                                    qa,
                                    index,
                                    0,
                                    validationErrorList1_2
                                )}
                                <br />
                            </div>
                        ))}
                    </div>

                    <h5 style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        Holistic Review of Performance
                    </h5>
                    <div>
                        {qaList.map((qa, index) => (
                            <div key={index}>
                                {getQAFromList(
                                    qa,
                                    index,
                                    1,
                                    validationErrorList
                                )}
                                <br />
                            </div>
                        ))}
                    </div>
                    <br />
                    <div>
                        {evalForm.isSubmitted ? (
                            <div>
                                <br />
                                <span style={{ float: 'left' }}>
                                    Evaluator Signature : Electronically Signed
                                    By&nbsp;{' '}
                                </span>
                                <br />
                                <span style={{ fontWeight: 'bold' }}>
                                    {evalForm.signerName} on{' '}
                                    {new Date(
                                        evalForm.signedDate
                                    ).toLocaleString()}
                                </span>
                                <br />
                                <span>
                                    {' '}
                                    Approved by the TMDSAS Administrator on{' '}
                                    {new Date(
                                        evalForm.signedDate
                                    ).toLocaleDateString()}
                                </span>
                                <span style={{ float: 'right' }}>
                                    Date Signed :{' '}
                                    {new Date(
                                        evalForm.signedDate
                                    ).toLocaleDateString()}
                                </span>
                            </div>
                        ) : null}
                        {!evalForm.isSubmitted ? (
                            <div>
                                <span style={{ padding: '10px' }}>
                                    <Button
                                        variant="contained"
                                        onClick={(e) => handleSave(e)}>
                                        Save
                                    </Button>
                                </span>
                                <span style={{ padding: '10px' }}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={(e) => handleSubmit(e)}>
                                        Submit
                                    </Button>
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default EvaluationLetterForm
